import { useState, useEffect, Fragment, FormEvent } from 'react';

export default function AdminPanelPage() {
  const [adminToken, setAdminToken] = useState<string | null>(localStorage.getItem('admin_token'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [tab, setTab] = useState<'conversions' | 'platform'>('conversions');
  const [stats, setStats] = useState<any>(null);
  const [exportMsg, setExportMsg] = useState('');
  const [exporting, setExporting] = useState(false);
  const [platformStats, setPlatformStats] = useState<any>(null);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [expandedOrgId, setExpandedOrgId] = useState<number | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [orgMsg, setOrgMsg] = useState('');
  const [orgBackups, setOrgBackups] = useState<any[]>([]);
  const [busyOrgId, setBusyOrgId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (adminToken) loadStats();
  }, [adminToken]);

  useEffect(() => {
    if (adminToken && tab === 'platform' && !platformStats) loadPlatformStats();
    if (adminToken && tab === 'platform') loadOrgBackups();
  }, [adminToken, tab]);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const res = await fetch(`${apiUrl}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (data.success && data.data?.token) {
        localStorage.setItem('admin_token', data.data.token);
        setAdminToken(data.data.token);
      } else {
        setLoginError(data.error || 'Login fehlgeschlagen');
      }
    } catch {
      setLoginError('Verbindung fehlgeschlagen');
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAdminToken(null);
    setStats(null);
  };

  const adminFetch = async (endpoint: string, options?: RequestInit) => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const res = await fetch(`${apiUrl}${endpoint}`, {
      ...options,
      headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json', ...options?.headers },
    });
    const data = await res.json();
    if (res.status === 401 || res.status === 403) {
      handleLogout();
      return null;
    }
    return data;
  };

  const loadStats = async () => {
    const data = await adminFetch('/admin/conversions/stats');
    if (data?.success) setStats(data.data);
  };

  const loadPlatformStats = async () => {
    setPlatformLoading(true);
    const data = await adminFetch('/admin/stats');
    if (data?.success) setPlatformStats(data.data);
    setPlatformLoading(false);
  };

  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso.replace(' ', 'T') + 'Z').toLocaleString('de-DE') : '—';

  const planLabel = (p: string) =>
    p === 'starter' ? 'Free (Starter)' : p === 'professional' ? 'Professional' : p === 'enterprise' ? 'Enterprise' : p;

  const handleExport = async () => {
    setExporting(true);
    setExportMsg('');
    const data = await adminFetch('/admin/conversions/export', { method: 'POST' });
    if (data?.success) {
      setExportMsg(data.message || `${data.data?.exported || 0} Conversions exportiert`);
      loadStats();
    }
    setExporting(false);
  };

  const handleDownload = async (filename: string) => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const res = await fetch(`${apiUrl}/admin/conversions/download/${encodeURIComponent(filename)}`, {
      headers: { 'Authorization': `Bearer ${adminToken}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // ---- Firmen-Verwaltung (Archivieren / Löschen / Import) ----

  const reloadPlatform = async () => {
    const data = await adminFetch('/admin/stats');
    if (data?.success) setPlatformStats(data.data);
  };

  const loadOrgBackups = async () => {
    const data = await adminFetch('/admin/organizations/backups');
    if (data?.success) setOrgBackups(data.data.backups || []);
  };

  const saveTextFile = (filename: string, text: string) => {
    const blob = new Blob([text], { type: 'application/sql' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleArchiveToggle = async (org: PlatformOrg) => {
    setBusyOrgId(org.id);
    setOrgMsg('');
    const action = org.archived_at ? 'unarchive' : 'archive';
    const data = await adminFetch(`/admin/organizations/${org.id}/${action}`, { method: 'POST' });
    if (data?.success) { setOrgMsg(data.message || 'Aktualisiert'); await reloadPlatform(); }
    else if (data) setOrgMsg(data.error || 'Fehler');
    setBusyOrgId(null);
  };

  const handleOrgBackup = async (org: PlatformOrg) => {
    setBusyOrgId(org.id);
    setOrgMsg('');
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const res = await fetch(`${apiUrl}/admin/organizations/${org.id}/backup`, {
      headers: { 'Authorization': `Bearer ${adminToken}` },
    });
    if (res.ok) {
      const text = await res.text();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="(.+?)"/);
      saveTextFile(m ? m[1] : `firma_${org.id}_backup.sql`, text);
      setOrgMsg(`Backup für "${org.name}" heruntergeladen`);
    } else {
      setOrgMsg('Backup fehlgeschlagen');
    }
    setBusyOrgId(null);
  };

  const handleOrgDelete = async (org: PlatformOrg) => {
    const confirmed = window.confirm(
      `Firma "${org.name}" endgültig löschen?\n\n` +
      `Alle Daten aus sämtlichen Modulen werden entfernt. Zuvor wird automatisch ` +
      `ein SQL-Backup heruntergeladen und serverseitig gespeichert, mit dem sich die ` +
      `Firma bei Bedarf wiederherstellen lässt.\n\nWirklich fortfahren?`
    );
    if (!confirmed) return;
    setBusyOrgId(org.id);
    setOrgMsg('');
    const data = await adminFetch(`/admin/organizations/${org.id}`, { method: 'DELETE' });
    if (data?.success) {
      if (data.data?.backupSql) saveTextFile(data.data.filename || `firma_${org.id}_backup.sql`, data.data.backupSql);
      setOrgMsg(data.message || 'Firma gelöscht');
      await reloadPlatform();
      await loadOrgBackups();
    } else if (data) {
      setOrgMsg(data.error || 'Fehler beim Löschen');
    }
    setBusyOrgId(null);
  };

  const handleImportFile = async (file: File) => {
    setImporting(true);
    setOrgMsg('');
    const sql = await file.text();
    const data = await adminFetch('/admin/organizations/import', {
      method: 'POST',
      body: JSON.stringify({ sql }),
    });
    if (data?.success) { setOrgMsg(data.message || 'Firma importiert'); await reloadPlatform(); await loadOrgBackups(); }
    else if (data) setOrgMsg(data.error || 'Import fehlgeschlagen');
    setImporting(false);
  };

  const handleBackupDownload = async (filename: string) => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const res = await fetch(`${apiUrl}/admin/organizations/backups/${encodeURIComponent(filename)}`, {
      headers: { 'Authorization': `Bearer ${adminToken}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // Login screen
  if (!adminToken) {
    return (
      <div style={{ fontFamily: "'72', Arial, sans-serif", minHeight: '100vh', background: '#1a1d21', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'white', borderRadius: '1rem', padding: '2.5rem', width: '100%', maxWidth: '400px', boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }}>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a1d21' }}>Admin-Panel</h1>
          <p style={{ color: '#6a7178', fontSize: '0.875rem', marginBottom: '1.5rem' }}>DSGVOeasy System-Administration</p>

          {loginError && (
            <div style={{ background: '#fef2f2', color: '#bb0000', padding: '0.75rem', borderRadius: '0.5rem', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.875rem' }}>Benutzername</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                disabled={loginLoading}
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.875rem' }}>Passwort</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                disabled={loginLoading}
                style={inputStyle}
              />
            </div>
            <button
              type="submit"
              disabled={loginLoading}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: 'none', background: '#1a1d21', color: 'white', fontWeight: 600, fontSize: '0.9375rem', cursor: 'pointer' }}
            >
              {loginLoading ? 'Anmeldung...' : 'Anmelden'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div style={{ fontFamily: "'72', Arial, sans-serif", minHeight: '100vh', background: '#f6f8fb' }}>
      {/* Header */}
      <div style={{ background: '#1a1d21', color: 'white', padding: '1rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: '1.125rem' }}>Admin-Panel</span>
          <span style={{ marginLeft: '1rem', opacity: 0.6, fontSize: '0.8125rem' }}>DSGVOeasy</span>
        </div>
        <button onClick={handleLogout} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '0.4rem 1rem', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
          Abmelden
        </button>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem' }}>
        {/* Tab navigation */}
        <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid #edf0f4', marginBottom: '1.5rem' }}>
          {([['conversions', 'Conversion-Export'], ['platform', 'Plattform-Statistik']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                background: 'none', border: 'none', padding: '0.75rem 1rem', fontSize: '0.9375rem',
                fontWeight: 600, cursor: 'pointer', color: tab === key ? '#1a1d21' : '#6a7178',
                borderBottom: tab === key ? '2px solid #0070f2' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'platform' ? (
          <PlatformStatsView
            data={platformStats}
            loading={platformLoading}
            expandedOrgId={expandedOrgId}
            onToggle={(id) => setExpandedOrgId(expandedOrgId === id ? null : id)}
            fmtDate={fmtDate}
            planLabel={planLabel}
            showArchived={showArchived}
            onToggleShowArchived={() => setShowArchived(v => !v)}
            orgMsg={orgMsg}
            busyOrgId={busyOrgId}
            importing={importing}
            backups={orgBackups}
            onArchiveToggle={handleArchiveToggle}
            onBackup={handleOrgBackup}
            onDelete={handleOrgDelete}
            onImportFile={handleImportFile}
            onBackupDownload={handleBackupDownload}
          />
        ) : (<>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem', color: '#1a1d21' }}>Google Ads Conversion-Export</h2>

        {exportMsg && (
          <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '0.75rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
            {exportMsg}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 120px', background: 'white', borderRadius: '0.75rem', padding: '1.25rem', textAlign: 'center', border: '1px solid #edf0f4' }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: '#0070f2' }}>{stats.pending}</div>
                <div style={{ fontSize: '0.8125rem', color: '#6a7178' }}>Pending</div>
              </div>
              <div style={{ flex: '1 1 120px', background: 'white', borderRadius: '0.75rem', padding: '1.25rem', textAlign: 'center', border: '1px solid #edf0f4' }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: '#36a41d' }}>{stats.exported}</div>
                <div style={{ fontSize: '0.8125rem', color: '#6a7178' }}>Exportiert</div>
              </div>
              <div style={{ flex: '1 1 120px', background: 'white', borderRadius: '0.75rem', padding: '1.25rem', textAlign: 'center', border: '1px solid #edf0f4' }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: '#8c9196' }}>{stats.revoked}</div>
                <div style={{ fontSize: '0.8125rem', color: '#6a7178' }}>Widerrufen</div>
              </div>
            </div>

            <button
              onClick={handleExport}
              disabled={exporting || stats.pending === 0}
              style={{
                padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none',
                background: stats.pending > 0 ? '#0070f2' : '#d1d5db', color: 'white',
                fontWeight: 600, fontSize: '0.9375rem', cursor: stats.pending > 0 ? 'pointer' : 'not-allowed',
                marginBottom: '2rem',
              }}
            >
              {exporting ? 'Wird exportiert...' : stats.pending > 0 ? `${stats.pending} Conversions jetzt exportieren` : 'Keine pending Conversions'}
            </button>

            {/* Export files */}
            {stats.exportFiles?.length > 0 && (
              <div>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem', color: '#1a1d21' }}>Bisherige Exporte</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '0.5rem', overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #edf0f4' }}>
                      <th style={thStyle}>Datei</th>
                      <th style={thStyle}>Größe</th>
                      <th style={thStyle}>Erstellt</th>
                      <th style={thStyle}>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.exportFiles.map((f: any) => (
                      <tr key={f.name} style={{ borderBottom: '1px solid #edf0f4' }}>
                        <td style={tdStyle}>{f.name}</td>
                        <td style={tdStyle}>{(f.size / 1024).toFixed(1)} KB</td>
                        <td style={tdStyle}>{new Date(f.created).toLocaleString('de-DE')}</td>
                        <td style={tdStyle}>
                          <button
                            onClick={() => handleDownload(f.name)}
                            style={{ background: 'none', border: '1px solid #0070f2', color: '#0070f2', padding: '0.25rem 0.75rem', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.8125rem' }}
                          >
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        </>)}
      </div>
    </div>
  );
}

type PlatformOrg = {
  id: number; name: string; plan: string;
  registered_at: string; plan_upgraded_at: string | null; archived_at: string | null;
  contact_person: string; contact_email: string; contact_phone: string | null;
  address: string; industry: string | null; employee_count: number | null;
  user_count: number; login_count: number; last_login_at: string | null;
};

type OrgBackup = { name: string; size: number; created: string };

function PlatformStatsView({
  data, loading, expandedOrgId, onToggle, fmtDate, planLabel,
  showArchived, onToggleShowArchived, orgMsg, busyOrgId, importing, backups,
  onArchiveToggle, onBackup, onDelete, onImportFile, onBackupDownload,
}: {
  data: {
    totalOrganizations: number; freeOrganizations: number; paidOrganizations: number;
    archivedOrganizations: number; organizations: PlatformOrg[];
  } | null;
  loading: boolean;
  expandedOrgId: number | null;
  onToggle: (id: number) => void;
  fmtDate: (iso: string | null) => string;
  planLabel: (p: string) => string;
  showArchived: boolean;
  onToggleShowArchived: () => void;
  orgMsg: string;
  busyOrgId: number | null;
  importing: boolean;
  backups: OrgBackup[];
  onArchiveToggle: (org: PlatformOrg) => void;
  onBackup: (org: PlatformOrg) => void;
  onDelete: (org: PlatformOrg) => void;
  onImportFile: (file: File) => void;
  onBackupDownload: (filename: string) => void;
}) {
  if (loading && !data) {
    return <div style={{ color: '#6a7178' }}>Wird geladen…</div>;
  }
  if (!data) return null;

  const visibleOrgs = data.organizations.filter(o => showArchived ? !!o.archived_at : !o.archived_at);

  return (
    <>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem', color: '#1a1d21' }}>Plattform-Statistik</h2>

      {orgMsg && (
        <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '0.75rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
          {orgMsg}
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 150px', background: 'white', borderRadius: '0.75rem', padding: '1.25rem', textAlign: 'center', border: '1px solid #edf0f4' }}>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: '#1a1d21' }}>{data.totalOrganizations}</div>
          <div style={{ fontSize: '0.8125rem', color: '#6a7178' }}>Aktive Firmen</div>
        </div>
        <div style={{ flex: '1 1 150px', background: 'white', borderRadius: '0.75rem', padding: '1.25rem', textAlign: 'center', border: '1px solid #edf0f4' }}>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: '#8c9196' }}>{data.freeOrganizations}</div>
          <div style={{ fontSize: '0.8125rem', color: '#6a7178' }}>Free (Starter)</div>
        </div>
        <div style={{ flex: '1 1 150px', background: 'white', borderRadius: '0.75rem', padding: '1.25rem', textAlign: 'center', border: '1px solid #edf0f4' }}>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: '#36a41d' }}>{data.paidOrganizations}</div>
          <div style={{ fontSize: '0.8125rem', color: '#6a7178' }}>Bezahlt</div>
        </div>
        <div style={{ flex: '1 1 150px', background: 'white', borderRadius: '0.75rem', padding: '1.25rem', textAlign: 'center', border: '1px solid #edf0f4' }}>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: '#c25a00' }}>{data.archivedOrganizations}</div>
          <div style={{ fontSize: '0.8125rem', color: '#6a7178' }}>Archivierte Firmen</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1d21', margin: 0 }}>
          {showArchived ? 'Archivierte Firmen' : 'Aktive Firmen'} ({visibleOrgs.length})
        </h3>
        <button
          onClick={onToggleShowArchived}
          style={{ background: showArchived ? '#c25a00' : 'none', border: '1px solid #c25a00', color: showArchived ? 'white' : '#c25a00', padding: '0.35rem 0.9rem', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600 }}
        >
          {showArchived ? 'Aktive anzeigen' : 'Archivierte anzeigen'}
        </button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '0.5rem', overflow: 'hidden' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #edf0f4' }}>
            <th style={thStyle}>Firma</th>
            <th style={thStyle}>Plan</th>
            <th style={thStyle}>Benutzer</th>
            <th style={thStyle}>Logins</th>
            <th style={thStyle}>Letzter Login</th>
            <th style={thStyle}>Registriert</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {visibleOrgs.length === 0 && (
            <tr><td colSpan={7} style={{ ...tdStyle, color: '#8c9196', textAlign: 'center', padding: '1.5rem' }}>
              {showArchived ? 'Keine archivierten Firmen.' : 'Keine aktiven Firmen.'}
            </td></tr>
          )}
          {visibleOrgs.map(o => (
            <Fragment key={o.id}>
              <tr style={{ borderBottom: '1px solid #edf0f4', background: o.archived_at ? '#fff8f2' : undefined }}>
                <td style={tdStyle}>
                  <strong>{o.name}</strong>
                  {o.archived_at && (
                    <span style={{ marginLeft: '0.5rem', padding: '0.1rem 0.4rem', borderRadius: '0.25rem', fontSize: '0.7rem', fontWeight: 700, background: '#f3e2d3', color: '#c25a00' }}>
                      Archiviert
                    </span>
                  )}
                </td>
                <td style={tdStyle}>
                  <span style={{
                    padding: '0.15rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: 600,
                    background: o.plan === 'starter' ? '#f1f2f4' : '#e8f5e9',
                    color: o.plan === 'starter' ? '#6a7178' : '#2e7d32',
                  }}>{planLabel(o.plan)}</span>
                </td>
                <td style={tdStyle}>{o.user_count}</td>
                <td style={tdStyle}>{o.login_count}</td>
                <td style={tdStyle}>{fmtDate(o.last_login_at)}</td>
                <td style={tdStyle}>{fmtDate(o.registered_at)}</td>
                <td style={tdStyle}>
                  <button onClick={() => onToggle(o.id)} style={{ background: 'none', border: '1px solid #0070f2', color: '#0070f2', padding: '0.25rem 0.75rem', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                    {expandedOrgId === o.id ? 'Schließen' : 'Details'}
                  </button>
                </td>
              </tr>
              {expandedOrgId === o.id && (
                <tr style={{ background: '#f9fafb' }}>
                  <td colSpan={7} style={{ padding: '1rem 1.5rem', fontSize: '0.875rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: '0.35rem', columnGap: '1rem' }}>
                      <div style={{ color: '#6a7178' }}>Kontaktperson</div><div>{o.contact_person || '—'}</div>
                      <div style={{ color: '#6a7178' }}>E-Mail</div><div>{o.contact_email || '—'}</div>
                      <div style={{ color: '#6a7178' }}>Telefon</div><div>{o.contact_phone || '—'}</div>
                      <div style={{ color: '#6a7178' }}>Adresse</div><div>{o.address || '—'}</div>
                      <div style={{ color: '#6a7178' }}>Branche</div><div>{o.industry || '—'}</div>
                      <div style={{ color: '#6a7178' }}>Mitarbeiteranzahl</div><div>{o.employee_count ?? '—'}</div>
                      <div style={{ color: '#6a7178' }}>Registriert am</div><div>{fmtDate(o.registered_at)}</div>
                      <div style={{ color: '#6a7178' }}>Plan-Upgrade</div>
                      <div>{o.plan === 'starter' ? '— (Free)' : fmtDate(o.plan_upgraded_at)}</div>
                      {o.archived_at && (<>
                        <div style={{ color: '#6a7178' }}>Archiviert am</div><div>{fmtDate(o.archived_at)}</div>
                      </>)}
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => onArchiveToggle(o)}
                        disabled={busyOrgId === o.id}
                        style={actionBtn('#c25a00')}
                      >
                        {o.archived_at ? 'Reaktivieren' : 'Archivieren'}
                      </button>
                      <button
                        onClick={() => onBackup(o)}
                        disabled={busyOrgId === o.id}
                        style={actionBtn('#0070f2')}
                      >
                        Backup (SQL) herunterladen
                      </button>
                      <button
                        onClick={() => onDelete(o)}
                        disabled={busyOrgId === o.id}
                        style={actionBtn('#bb0000')}
                      >
                        {busyOrgId === o.id ? 'Bitte warten…' : 'Löschen (mit Backup)'}
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>

      {/* Import + Backups */}
      <div style={{ marginTop: '2.5rem', background: 'white', border: '1px solid #edf0f4', borderRadius: '0.75rem', padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a1d21' }}>Firma aus Backup importieren</h3>
        <p style={{ fontSize: '0.8125rem', color: '#6a7178', marginBottom: '1rem' }}>
          Wählen Sie eine zuvor erstellte <code>.sql</code>-Sicherung aus, um eine gelöschte Firma inkl. aller Module wiederherzustellen.
        </p>
        <label style={{ display: 'inline-block' }}>
          <span style={{ ...actionBtn('#36a41d'), display: 'inline-block', opacity: importing ? 0.6 : 1 }}>
            {importing ? 'Wird importiert…' : 'SQL-Backup auswählen…'}
          </span>
          <input
            type="file"
            accept=".sql,application/sql,text/plain"
            disabled={importing}
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImportFile(f);
              e.target.value = '';
            }}
          />
        </label>

        {backups.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <h4 style={{ fontSize: '0.9375rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a1d21' }}>Serverseitige Backups</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #edf0f4' }}>
                  <th style={thStyle}>Datei</th>
                  <th style={thStyle}>Größe</th>
                  <th style={thStyle}>Erstellt</th>
                  <th style={thStyle}>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {backups.map(b => (
                  <tr key={b.name} style={{ borderBottom: '1px solid #edf0f4' }}>
                    <td style={{ ...tdStyle, wordBreak: 'break-all' }}>{b.name}</td>
                    <td style={tdStyle}>{(b.size / 1024).toFixed(1)} KB</td>
                    <td style={tdStyle}>{new Date(b.created).toLocaleString('de-DE')}</td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => onBackupDownload(b.name)}
                        style={{ background: 'none', border: '1px solid #0070f2', color: '#0070f2', padding: '0.25rem 0.75rem', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.8125rem' }}
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

const actionBtn = (color: string): React.CSSProperties => ({
  background: 'none', border: `1px solid ${color}`, color, padding: '0.4rem 0.9rem',
  borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600,
});

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.7rem 0.875rem', borderRadius: '0.5rem',
  border: '1.5px solid #d1d5db', fontSize: '0.9375rem', outline: 'none',
  fontFamily: 'inherit', boxSizing: 'border-box',
};

const thStyle: React.CSSProperties = {
  padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.8125rem', fontWeight: 600, color: '#6a7178',
};

const tdStyle: React.CSSProperties = {
  padding: '0.75rem 1rem', fontSize: '0.875rem',
};
